import styled, { css } from 'styled-components';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding: 0 4px;
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }

  span.subtitle {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

type StatTone = 'brand' | 'info' | 'warning' | 'neutral';

const STAT_TONES: Record<
  StatTone,
  { iconBg: string; iconColor: string; valueColor: string }
> = {
  brand: {
    iconBg: '#e6f3ec',
    iconColor: '#006633',
    valueColor: '#0f172a',
  },
  info: {
    iconBg: '#e0ecff',
    iconColor: '#1d4ed8',
    valueColor: '#0f172a',
  },
  warning: {
    iconBg: '#fff4e0',
    iconColor: '#b45309',
    valueColor: '#0f172a',
  },
  neutral: {
    iconBg: '#f1f5f9',
    iconColor: '#475569',
    valueColor: '#0f172a',
  },
};

export const StatCard = styled.div<{ $tone: StatTone }>`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
    transform: translateY(-1px);
  }

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${STAT_TONES[$tone].iconBg};
      color: ${STAT_TONES[$tone].iconColor};
    `}
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    ${({ $tone }) => css`
      color: ${STAT_TONES[$tone].valueColor};
    `}
  }

  .helper {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
  }
`;

/* ─── DataTable cell helpers ─── */

export const CompanyCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  .avatar {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #006633 0%, #00874a 100%);
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    letter-spacing: -0.02em;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .name {
    font-size: 13.5px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }

  .razao {
    font-size: 11.5px;
    color: #94a3b8;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }
`;

export const MutedCell = styled.span`
  color: #475569;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

export const LocationCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 13px;

  svg {
    color: #94a3b8;
    flex-shrink: 0;
  }
`;

export const SyndicateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  color: #334155;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  max-width: 200px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: #006633;
    flex-shrink: 0;
  }
`;

export const CountChip = styled.span<{ $tone?: 'brand' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
  ${({ $tone = 'neutral' }) => {
    if ($tone === 'brand')
      return css`
        background: #e6f3ec;
        color: #006633;
      `;
    if ($tone === 'info')
      return css`
        background: #e0ecff;
        color: #1d4ed8;
      `;
    return css`
      background: #f1f5f9;
      color: #475569;
    `;
  }}
`;

/* ─── Buttons ─── */

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: none;
  background: #006633;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #00451f;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

export const RowAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 6px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    ${({ $danger }) =>
      $danger
        ? css`
            background: #fef2f2;
            border-color: #b91c1c;
            color: #b91c1c;
          `
        : css`
            background: #f8fafc;
            border-color: #006633;
            color: #006633;
          `}
  }
`;

/* ─── Modal ─── */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const FieldGroup = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select {
    height: 40px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
  }
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #006633;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
`;
